import { toJS } from 'mobx';

import { RouterStore } from '@estee/elc-router';

import { IFeatureProcessResults, IUserState, UserStatusKey } from '~api/RampPgApiSdk';
import { genericErrorMessage, OTP_FLOW, OTP_TYPE_SMS, StorageKey } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import { otpTypeMap, UserIdType } from '~domain/entities/Config';
import { OnOtpSuccessCallback } from '~domain/entities/OtpStore';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import { ACCOUNT_HOME_IKALAR_ANCHOR, SOCIAL_SIGN_UP_SUCCESS_ROUTE } from '~routes/routes';
import { UserStateResolverService } from '~services/UserStateResolverService';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { UserAlreadyRegisteredUseCase } from '~use-cases/UserAlreadyRegisteredUseCase';
import {
    emitAnalyticsEvent,
    eventTag,
    GetSignUpEventDataParameterType,
    SignUpTaggingEventType
} from '~utils/Analytics';
import { removeLocalStorageItems, setLocalStorageItem } from '~utils/LocalStorageUtils';
import { checkIsMobileDevice } from '~utils/mobile';
import {
    getSocialProviderFromStorage,
    getSocialSignUpProviderSource,
    getSocialSignUpProviderSourceFromState
} from '~utils/SocialUtils';
import { IdentifierChangedKeyType } from '~domain/entities/ConsentForm';
import { JourneyType } from '~constants/Journeys';

export class SocialConsentSubmittedUseCase {
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private routerStore: RouterStore;
    private messageRepository: MessageRepository;
    // To be initialized with the result from POST user validate_only mode, see IValidateAddUserResponse
    private validateResultResolver: UserStateResolverService;

    constructor() {
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.configRepository = diContainer.get(serviceNames.configRepository);
        this.routerStore = diContainer.get(serviceNames.routerStore);
        this.messageRepository = diContainer.get(serviceNames.messageRepository);
    }

    // when either of the following is true:
    // 1) multiple ELC accounts exist;
    // 2) identifier conflict (could happen when only one ELC account is matched), except conflict on single partial user;
    // take the user to sign-in page, after signing in, when the signed in account is among above
    // matched accounts, update user profile
    needSignInToContinue = (users: IUserState[]) => {
        if (!users.length) {
            // not matching to any exiting user account
            return false;
        }

        if (this.validateResultResolver.isMultipleAccountsFound) {
            return true;
        }

        // one user account (even users.length is 2), one status
        const { userStatusKey } = users[0];
        const {
            consentForm: { isSocialProfileVerified }
        } = this.userRepository;

        // connecting to existing partial online user requires no social profile verification and doesn't matter if there's any conflict -
        // actually there won't be any since online partial user is email only
        if (userStatusKey === UserStatusKey.EXISTING_PARTIAL) {
            return false;
        }

        // determine if can proceed when 1 account is matched
        // users[0].identifierConflict && this.validateResultResolver.isCompleteUserFound;

        const isSingleCompleteAccountAndConflicted =
            users.some((user) => !!user.identifierConflict) &&
            this.validateResultResolver.isCompleteUserFound;

        const partialOnOTPFieldWithoutConflicted =
            this.validateResultResolver.hasPartialProtectedUser() &&
            this.getOTPFields().includes(users[0].userIdType as UserIdType) &&
            users.some((user) => !user.identifierConflict);

        if (partialOnOTPFieldWithoutConflicted) {
            return false;
        }

        return !users.some(isSocialProfileVerified) || isSingleCompleteAccountAndConflicted;
    };

    private isOtpRequired = (users: IUserState[]) => {
        const {
            consentForm: { isIdentityValueChanged, users: socialProfileMatchedUsers }
        } = this.userRepository;

        const otpFields = this.getOTPFields();
        if (otpFields.length === 0) {
            return false;
        }
        const ignoreOTP = this.ignoreOTP(users);
        if (ignoreOTP) {
            return false;
        }

        // need otp if otp fields/identifier is manually changed
        const isOtpOnIdentifierChange = [UserIdType.EMAIL_ADDRESS, UserIdType.MOBILE_NUMBER].some(
            (type: IdentifierChangedKeyType) =>
                otpFields?.includes(type) && isIdentityValueChanged[type]
        );

        return (
            // Some social provider like LINE/Facebook, it won't provide mandatory identifier values;
            // or some market allow changing identifier values provided by social providers.
            // TODO: APACRIGEL-2435 should only check if OTP type related fields changed
            isOtpOnIdentifierChange ||
            !socialProfileMatchedUsers.some(
                ({ userIdType, isVerified }) => isVerified && otpFields?.includes(userIdType)
            )
        );
    };

    // [] for otp is not configured or disabled
    private getOTPFields = () => {
        const { otpConfig, iamConfig: { turnOffOtp } = {} } = this.configRepository;

        if (!otpConfig || turnOffOtp) {
            return [];
        }

        return otpTypeMap.get(otpConfig.otpType) || [];
    };

    private ignoreOTP = (users: IUserState[]) => {
        // don't enforce otp if PP email(with mobile verified) + New mobile (APACRIGEL-3446 concern4)
        // it will continue to update user and redirect
        const conflictedPartialOnNonOTPField =
            this.validateResultResolver.hasPartialProtectedUser() &&
            !this.getOTPFields().includes(users[0].userIdType as UserIdType) &&
            users.some((user) => !!user.identifierConflict);

        return conflictedPartialOnNonOTPField;
    };

    private startOtp = (onOtpSuccess: OnOtpSuccessCallback) => {
        const { otpStore } = this.userRepository;

        otpStore.setCallback(onOtpSuccess);
        // TODO: APACRIGEL-2453 determine OTP type correctly, currently only
        // KR has mobile OTP for social
        otpStore.setData({
            showOtp: true,
            type: OTP_TYPE_SMS,
            flow: OTP_FLOW.SIGN_UP
        });
    };

    private showError = (error?: string) => {
        this.messageRepository.setMessages(MessageGroup.SIGN_UP, [
            { transKey: error || genericErrorMessage }
        ]);
    };

    // https://confluence.esteeonline.com/pages/viewpage.action?pageId=319190381&preview=/319190381/319190390/JM%20KR%20RAMP%20on%20Brand.com.png
    // And there will be 3 different handling for consent form submit
    // 1. when user is not found - create an ELC account and connect
    // 2. when user's identifier is not updated - update account and connect
    // 3. when user's identifier is changed:
    // 3a. found matched completed user - redirect to sign-in, after sign-in successfully, connect
    // 3b. no complete user found - create new user or update existing user, connect
    execute = async () => {
        const {
            userFormStore: { values, setSubmitting },
            signedInUserId,
            validateSignUp
        } = this.userRepository;
        if (signedInUserId) {
            await this.updateUser(signedInUserId);

            return;
        }

        // use validate_only mode to check if any ELC accounts exist according to the identifier
        // values provided
        const { users, error } = await validateSignUp(toJS(values));

        // validate_mode only call failed
        if (error) {
            this.showError(error);
            setSubmitting(false);

            return;
        }

        this.validateResultResolver = new UserStateResolverService(users);

        if (this.needSignInToContinue(users)) {
            this.goToSignInPage();
        } else {
            // one existing partial or complete user at most
            let next: () => Promise<void>;
            // determine create/update account accordingly
            if (users.length === 0) {
                next = this.createUser;
            } else {
                // TODO: APACRIGEL-2052 consider allowPartialOfflineUserEdit flag
                // userId will be the same here even users.length is 2
                const userId = users[0].userId;
                next = this.updateUser.bind(this, userId);
            }

            if (this.isOtpRequired(users)) {
                this.startOtp(next);
            } else {
                await next();
            }
        }
        setSubmitting(false);
    };

    private createUser = async () => {
        const {
            setRegisteredUser,
            signUp,
            userFormStore: { setSubmitting, values }
        } = this.userRepository;

        const formValues = toJS(values);

        // since we already used validate_only mode to check if there's any existing ELC account
        // sign-up should succeed
        const result = await signUp(formValues);
        const { users } = result;

        let newUserId;
        if (users.length === 1) {
            const { userStatusKey, userId } = users[0];
            if (
                [UserStatusKey.NEW_COMPLETE, UserStatusKey.NEW_COMPLETE_OFFLINE].includes(
                    userStatusKey
                )
            ) {
                newUserId = userId;
            }
        }

        if (newUserId) {
            emitAnalyticsEvent(
                eventTag.SIGN_UP_SUCCESSFUL,
                this.getSignUpEventData({ userId: newUserId })
            );
            this.handleFeatureProcessResult(result);
            setRegisteredUser(users[0]);
            await this.complete();
        } else {
            this.showError();
            setSubmitting(false);
            emitAnalyticsEvent(
                eventTag.SIGN_UP_FAILED,
                this.getSignUpEventData({ type: SignUpTaggingEventType.API_ERROR_OR_IP_LOCK })
            );
        }
    };

    private backupExistingUserValuesToLocalStorage = () => {
        const {
            userFormStore: { values },
            consentForm: { setFormValuesBackup, firstCompleteAccount: user }
        } = this.userRepository;

        const formValues = JSON.stringify(values);
        // need to backup user id and form values before redirecting to the 3PP site.
        setLocalStorageItem(StorageKey.CONNECT_USER_ID, String(user?.userId));
        setLocalStorageItem(StorageKey.FORM_VALUES, formValues);
        this.configRepository.setFormerSelectedProvider(getSocialProviderFromStorage());
        setFormValuesBackup(JSON.parse(formValues));
    };

    private goToSignInPage() {
        const { setIsSocialProfile } = this.configRepository;
        const {
            userFormStore: { setSubmitting }
        } = this.userRepository;

        this.backupExistingUserValuesToLocalStorage();

        setSubmitting(false);
        setIsSocialProfile(false);
        emitAnalyticsEvent(
            eventTag.SIGN_UP_FAILED,
            this.getSignUpEventData({ type: SignUpTaggingEventType.USER_EXISTING })
        );

        new UserAlreadyRegisteredUseCase(
            this.validateResultResolver.users as IUserState[]
        ).execute();
    }

    private updateUser = async (userId: number) => {
        const {
            setRegisteredUser,
            updateUser,
            userFormStore: { values }
        } = this.userRepository;

        const result = await updateUser(userId, toJS(values));
        this.handleFeatureProcessResult(result);
        setRegisteredUser(result.user);

        // TODO APACRIGEL-2052 update failure not handled
        emitAnalyticsEvent(eventTag.SIGN_IN_SUCCESSFUL, this.getSignInEventData());
        await this.complete();
    };

    private handleFeatureProcessResult = ({ posIKalarCallbackUrl }: IFeatureProcessResults) => {
        if (posIKalarCallbackUrl) {
            this.userRepository.setPosIKalarCallbackUrl(posIKalarCallbackUrl);
        }
    };

    private complete = async () => {
        const {
            onSuccessRedirectUrl,
            iamConfig,
            journey: { postSubmitRoute }
        } = this.configRepository;

        if (iamConfig.submitJourney === JourneyType.IN_STORE) {
            removeLocalStorageItems([
                StorageKey.ACCOUNT_REDIRECT_URL,
                StorageKey.SOCIAL_DISPLAY_NAME,
                StorageKey.SOCIAL_EVENT_SOURCE,
                StorageKey.SOCIAL_STATE
            ]);
            this.routerStore.changeCurrentRoute(postSubmitRoute);

            return;
        }

        const socialProvider = this.configRepository.socialProvider as SignInType;
        const {
            postAuthCode,
            signedInUserId,
            posIKalarCallbackUrl,
            consentForm: { callbackCode, callbackState, setIsConsentFlow }
        } = this.userRepository;

        if (!signedInUserId) {
            await postAuthCode({
                authCode: callbackCode,
                socialProvider,
                state: callbackState,
                providerSource: getSocialSignUpProviderSource()
            });
        }
        setIsConsentFlow(false);

        // Redirect back to source page or goto `redirectUrl`
        const { changeLocation } = this.routerStore;
        // If there is provider source from social callback state,
        // It is social sign up flow, need go to register success page
        // APACRIGEL-2052 use "isSocialSignUp" flag directly
        if (getSocialSignUpProviderSourceFromState(callbackState)) {
            this.routerStore.changeCurrentRoute(SOCIAL_SIGN_UP_SUCCESS_ROUTE);
        } else {
            changeLocation(
                posIKalarCallbackUrl && checkIsMobileDevice()
                    ? ACCOUNT_HOME_IKALAR_ANCHOR
                    : onSuccessRedirectUrl
            );
        }
    };

    // TODO: APACRIGEL-2255 create a service/decorator to handle building analytics payload
    private getSignUpEventData = ({
        userId,
        type: failureType
    }: GetSignUpEventDataParameterType) => {
        const {
            signUpEventSource: source,
            consentForm: { provider }
        } = this.userRepository;
        const type: SignUpTaggingEventType = failureType ?? SignUpTaggingEventType.COMPLETE;

        return {
            source,
            type,
            provider,
            ...(userId ? { userId } : null)
        };
    };

    private getSignInEventData = () => {
        const {
            signInEventSource: source,
            consentForm: { provider }
        } = this.userRepository;

        return {
            source,
            provider
        };
    };
}
